import { defineStore } from 'pinia';
import { ModuleOption } from '@/stores/moduleConst';
import { BaseStore } from '@/stores/baseStore';
import dbOptionAPI from '@/apis/platform/dbOptionAPI';
import commonFunction from '@/commons/commonFunction';
import { useContextStore } from './contextStore';

const dbOptionStore = new BaseStore(dbOptionAPI);

export const useDbOptionStore = defineStore(ModuleOption, {
  state: () => ({
    ...dbOptionStore.state,
    _config: {},
    moduleName: ModuleOption,
    // Thêm cache cho dbOptions
    _dbOptionsCache: null,
    _dbOptionsCacheExpiry: null,
  }),

  // Thêm persist config để cache tồn tại qua các lần reload
  persist: {
    storage: localStorage,
    pick: ['_dbOptionsCache', '_dbOptionsCacheExpiry'], // Chỉ persist cache, không persist promise
    key: 'dbOptions-cache',
  },

  actions: {
    ...dbOptionStore.action,

    /**
     * @description: Lấy tất cả các option từ cache/database
     * @returns: {Array} array of options
     */
    async getDbOptions() {
      try {
        const result = await this._fetchDbOptions();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    /**
     * @description: Gọi API để lấy tất cả các option từ database
     * @returns: {Array} array of options
     */
    async _fetchDbOptions() {
      try {
        commonFunction.mask();
        this._dbOptionsCache = await dbOptionAPI.getDbOptions();
        return this._dbOptionsCache;
      } finally {
        commonFunction.unmask();
      }
    },

    /**
     * @description: Clear Cache DB Options
     */
    clearDbOptionsCache() {
      this._dbOptionsCache = null;
      this._dbOptionsCacheExpiry = null;
    },

    /**
     * @description: Update option từ database
     * @param: {Object} data - data of option
     * @returns: {Object} result of update
     */
    async updateDbOption(data) {
      try {
        commonFunction.mask();
        const result = await dbOptionAPI.updateDbOption(data);

        // Thay vì clear cache, hãy update cache thông minh
        this._updateCacheAfterDbOptionUpdate(data, result);

        return result;
      } catch (error) {
        console.log(error);
        // Chỉ clear cache khi có lỗi
        this.clearDbOptionsCache();
        throw error;
      } finally {
        commonFunction.unmask();
      }
    },

    /**
     * @description: Update option từ database update theo cả user_id và tenant_id
     * @param: {Object} data - data of option
     * @returns: {Object} result of update
     */
    async UpdateDbOptionByUserAndTenantId(data) {
      try {
        commonFunction.mask();
        const result = await dbOptionAPI.UpdateDbOptionByUserAndTenantId(data);

        // Thay vì clear cache, hãy update cache thông minh
        this._updateCacheAfterDbOptionUpdate(data, result);

        return result;
      } catch (error) {
        console.log(error);
        // Chỉ clear cache khi có lỗi
        this.clearDbOptionsCache();
        throw error;
      } finally {
        commonFunction.unmask();
      }
    },

    /**
     * @description: Update cache thông minh sau khi update option
     * @param: {Object} updateData - data of option
     * @param: {Object} apiResult - result of update
     * vqphong - 11/08/2025
     */
    _updateCacheAfterDbOptionUpdate(updateData, apiResult) {
      // Kiểm tra xem cache có tồn tại không
      if (!this._dbOptionsCache || !Array.isArray(this._dbOptionsCache)) {
        return;
      }

      // Tìm option trong cache cần update
      const optionIndex = this._dbOptionsCache.findIndex(option => option.option_name === updateData.option_name);

      if (optionIndex !== -1) {
        // Update option đã tồn tại trong cache
        this._dbOptionsCache[optionIndex] = {
          ...this._dbOptionsCache[optionIndex],
          option_value: updateData.option_value,
          modified_date: new Date().toISOString(),
          // Merge thêm dữ liệu từ API response nếu có
          ...(apiResult?.data || {}),
        };
      } else {
        // Thêm option mới vào cache nếu chưa tồn tại
        const newOption = {
          option_name: updateData.option_name,
          option_value: updateData.option_value,
          tenant_id: updateData.tenant_id,
          created_date: new Date().toISOString(),
          modified_date: new Date().toISOString(),
          // Merge thêm dữ liệu từ API response nếu có
          ...(apiResult?.data || {}),
        };
        this._dbOptionsCache.push(newOption);
      }

      console.log(`Cache đã được cập nhật cho option: ${updateData.option_name}`);
    },

    /**
     * @description: Lấy option_value dựa trên option_name
     * @param: {string} optionName - tên option cần tìm
     * @return: {string|null} option_value hoặc null nếu không tìm thấy
     * CreatedBy: htthoa 9.7.2025
     */
    async getOptionValue(optionName, isFromUser = false, byUserTenant = false) {
      try {
        commonFunction.mask();
        // Sử dụng getDbOptions() đã có cache thay vì gọi trực tiếp API
        const result = await this.getDbOptions();

        if (result && Array.isArray(result)) {
          var data = result;
          //nếu option nào lưu theo user thì lọc theo user k lọc tất
          if (isFromUser == true) {
            const contextStore = useContextStore()
            data = data.filter(x => x.user_id == contextStore?.context?.user.user_id)
          }
          if(byUserTenant == true){
            const contextStore = useContextStore()
            data = data.filter(x => x.user_id == contextStore?.context?.user.user_id && x.tenant_id == contextStore?.context?.Context?.TenantId)
          }
          // Tìm object đầu tiên có option_name trùng khớp
          const foundOption = data.find(option => option.option_name === optionName);

          if (foundOption) {
            return foundOption.option_value;
          }
        }

        return null;
      } catch (error) {
        console.error('Lỗi khi lấy option_value:', error);
        return null;
      } finally {
        commonFunction.unmask();
      }
    },

    /**
     * @description: Lấy option_value dựa trên option_name (synchronous version từ cache)
     * @param: {string} optionName - tên option cần tìm
     * @return: {string|null} option_value hoặc null nếu không tìm thấy
     * CreatedBy: vqphong - 11/08/2025
     */
    getDbOptionValue(optionName) {
      try {
        // Kiểm tra cache trước
        if (this._dbOptionsCache && Array.isArray(this._dbOptionsCache)) {
          const foundOption = this._dbOptionsCache.find(option => option.option_name === optionName);
          if (foundOption) {
            return foundOption.option_value;
          }
        }
        return null;
      } catch (error) {
        console.error('Lỗi khi lấy option_value từ cache:', error);
        return null;
      }
    },
  },
});
